import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';

/**
 * Meilisearch-alapú autocomplete kereső
 * @param {string} value - Keresési kifejezés (kontrollált)
 * @param {function} onChange - Érték változás callback
 * @param {function} onSearch - Keresés indítása (Enter/gomb)
 * @param {object} sx - MUI sx prop az input mezőre
 * @param {object} inputProps - Extra TextField props
 */
const SearchAutocomplete = ({ value, onChange, onSearch, onSuggestionSelect, endAdornment, iconColor, sx = {}, ...inputProps }) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const containerRef = useRef(null);
  const isUserTypingRef = useRef(false);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/search/autocomplete?q=${encodeURIComponent(query.trim())}&limit=6`,
        { signal: abortRef.current.signal }
      );
      const data = await res.json();
      setSuggestions(data.hits || []);
      setOpen(isUserTypingRef.current && (data.hits || []).length > 0);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (newValue) => {
    isUserTypingRef.current = true;
    onChange(newValue);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 250);
  };

  // Kattintás a javaslatra → keresés futtatása + quick view megnyitása
  const handleSuggestionClick = (provider) => {
    isUserTypingRef.current = false;
    if (abortRef.current) abortRef.current.abort();
    clearTimeout(debounceRef.current);
    setOpen(false);
    setSuggestions([]);
    if (onSuggestionSelect) {
      onSuggestionSelect(provider);
    } else if (onSearch) {
      onSearch();
    }
  };

  // Enter / keresés gomb
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      isUserTypingRef.current = false;
      if (abortRef.current) abortRef.current.abort();
      clearTimeout(debounceRef.current);
      setOpen(false);
      setSuggestions([]);
      if (onSearch) onSearch();
    }
  };

  // Külső kattintásra bezárás
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', ...sx }}>
      <TextField
        fullWidth
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => { isUserTypingRef.current = false; setFocused(false); }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: focused ? undefined : (iconColor ?? 'rgba(255,255,255,0.85)'), transition: 'color 0.2s' }} />
              <CircularProgress size={12} sx={{ ml: 0.5, opacity: 0.5, visibility: loading ? 'visible' : 'hidden' }} />
            </InputAdornment>
          ),
          ...(endAdornment && {
            endAdornment: (
              <InputAdornment position="end">
                {endAdornment}
              </InputAdornment>
            ),
          }),
        }}
        {...inputProps}
      />

      {open && focused && suggestions.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            mt: 0.5,
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          <List disablePadding>
            {suggestions.map((provider, idx) => (
              <React.Fragment key={provider.id}>
                {idx > 0 && <Divider />}
                <ListItem
                  button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionClick(provider)}
                  sx={{
                    py: 1.2,
                    '&:hover': { bgcolor: 'action.hover' },
                    cursor: 'pointer',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={provider.imageUrl || undefined}
                      variant="rounded"
                      sx={{ width: 44, height: 44, borderRadius: 1 }}
                    >
                      {provider.businessName?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        dangerouslySetInnerHTML={{
                          __html:
                            provider._formatted?.businessName || provider.businessName,
                        }}
                        sx={{ '& mark': { bgcolor: 'transparent', fontWeight: 700 } }}
                      />
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {[provider.categoryName, provider.city].filter(Boolean).join(' · ')}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SearchAutocomplete;
